package twist.mena.sports.admin.screens.notifications

import androidx.compose.runtime.*
import kotlin.js.dateLocaleOptions
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminNotificationSettingsApi
import twist.mena.sports.admin.api.HistoryFilters
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminNotificationEventType
import twist.mena.sports.shared.dto.admin.AdminNotificationGroup
import twist.mena.sports.shared.dto.admin.AdminNotificationHistoryResponse
import twist.mena.sports.shared.dto.admin.OffsetPage

private const val PAGE_SIZE = 50

/**
 * Every notification from every source (vendor events, admin and scheduled sends),
 * including the ones that were skipped and why.
 */
@Composable
fun HistoryTab(api: AdminNotificationSettingsApi) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminNotificationHistoryResponse>>>(UiState.Loading) }
    var draft by remember { mutableStateOf(HistoryFilters()) }
    var applied by remember { mutableStateOf(HistoryFilters()) }
    var offset by remember { mutableStateOf(0) }
    var groups by remember { mutableStateOf<List<AdminNotificationGroup>>(emptyList()) }
    var eventTypes by remember { mutableStateOf<List<AdminNotificationEventType>>(emptyList()) }

    fun load() {
        scope.launch {
            state = UiState.Loading
            state = try {
                UiState.Success(api.getHistory(applied.withIsoDates(), offset, PAGE_SIZE))
            } catch (e: Exception) {
                UiState.Error(e.message ?: "Failed to load history")
            }
        }
    }

    LaunchedEffect(Unit) {
        try {
            val settings = api.getSettings()
            groups = settings.groups
            eventTypes = settings.eventTypes
        } catch (_: Exception) { /* filters fall back to free text */ }
    }
    LaunchedEffect(applied, offset) { load() }

    // Filters
    Div({
        style {
            cardStyle(colors)
            display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(Spacing.md)
            alignItems(AlignItems.FlexEnd); marginBottom(Spacing.lg)
        }
    }) {
        FilterSelect("Source", draft.source, listOf("" to "All", "vendor" to "Vendor", "admin" to "Admin", "system" to "System")) {
            draft = draft.copy(source = it)
        }
        FilterSelect("Toggle", draft.group, listOf("" to "All") + groups.map { it.code to it.nameEn }) {
            draft = draft.copy(group = it)
        }
        FilterSelect("Event", draft.eventCode, listOf("" to "All") + eventTypes.map { it.code to (it.nameEn ?: it.code) }) {
            draft = draft.copy(eventCode = it)
        }
        FilterSelect(
            "Status", draft.status,
            listOf("" to "All", "sent" to "Sent", "skipped" to "Skipped", "failed" to "Failed", "pending" to "Pending", "queued" to "Queued", "delivered" to "Delivered")
        ) { draft = draft.copy(status = it) }
        FilterSelect(
            "Entity type", draft.entityType,
            listOf("" to "Any", "team" to "Team", "league" to "League", "player" to "Player", "match" to "Match", "article" to "Article")
        ) { draft = draft.copy(entityType = it) }
        FilterInput("Entity id", draft.entityRef, "id", 110) { draft = draft.copy(entityRef = it) }
        FilterInput("From", draft.from, "", 180, InputType.DateTimeLocal) { draft = draft.copy(from = it) }
        FilterInput("To", draft.to, "", 180, InputType.DateTimeLocal) { draft = draft.copy(to = it) }
        FilterInput("Text", draft.query, "title or body", 180) { draft = draft.copy(query = it) }

        Button({
            onClick { offset = 0; applied = draft }
            style { primaryButtonStyle(colors); padding(Spacing.sm, Spacing.lg) }
        }) { Text("Apply") }
        Button({
            onClick { draft = HistoryFilters(); applied = HistoryFilters(); offset = 0 }
            style { secondaryButtonStyle(colors); padding(Spacing.sm, Spacing.lg) }
        }) { Text("Reset") }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading…") }
        is UiState.Error -> Div({ style { errorMessageStyle(colors) } }) { Text(s.message) }
        is UiState.Success -> {
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm); marginBottom(Spacing.sm) } }) {
                Text("${s.data.total} notifications")
            }
            if (s.data.data.isEmpty()) {
                Div({ style { cardStyle(colors); color(Color(colors.textMuted)) } }) { Text("No notifications match these filters.") }
            } else {
                DataTable(
                    columns = historyColumns(colors),
                    rows = s.data.data,
                    total = s.data.total,
                    offset = s.data.offset,
                    limit = s.data.limit,
                    onPageChange = { offset = it }
                )
            }
        }
    }
}

private fun historyColumns(colors: AppColors): List<Column<AdminNotificationHistoryResponse>> = listOf(
    Column("Created (Cairo)") { Text(cairoTime(it.createdAtUtc)) },
    Column("Source") { row ->
        Span({ style { badgeStyle(colors.surfaceHover, colors.text) } }) { Text(row.source) }
    },
    Column("Event") { row ->
        Div { Text(row.eventCode ?: row.type) }
        row.groupCode?.let { group ->
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.xs) } }) { Text(group) }
        }
    },
    Column("Title / body") { row ->
        Div({ style { fontWeight("600"); property("direction", "auto") } }) { Text(row.titleAr) }
        if (row.titleEn != row.titleAr) Div { Text(row.titleEn) }
        (row.bodyAr ?: row.bodyEn)?.takeIf { it.isNotBlank() }?.let { body ->
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text(body) }
        }
    },
    Column("Related") { row ->
        if (row.entities.isEmpty()) Text(row.audience)
        else row.entities.forEach { entity ->
            Div({ style { fontSize(Typography.sm); property("white-space", "nowrap") } }) { Text("${entity.type} ${entity.id}") }
        }
    },
    Column("Status") { row ->
        val statusColor = when (row.deliveryStatus) {
            "sent", "delivered" -> colors.success
            "failed" -> colors.danger
            "skipped" -> colors.warning
            else -> colors.textMuted
        }
        Div({ style { color(Color(statusColor)); fontWeight("600") } }) { Text(row.deliveryStatus) }
        (row.skipReason ?: row.failedReason)?.let { reason ->
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.xs) } }) { Text(reason) }
        }
    },
    Column("Sender / vendor id") { row ->
        Div({ style { fontSize(Typography.sm) } }) { Text(row.adminSenderId ?: row.vendorEventId ?: "system") }
    }
)

@Composable
private fun FilterSelect(label: String, value: String, options: List<Pair<String, String>>, onChange: (String) -> Unit) {
    val colors = AppTheme.colors
    Div {
        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) { Text(label) }
        Select({
            onChange { onChange(it.value ?: "") }
            style { formInputStyle(colors); width(150.px) }
        }) {
            options.forEach { (key, text) ->
                Option(key, { if (key == value) attr("selected", "") }) { Text(text) }
            }
        }
    }
}

@Composable
private fun FilterInput(
    label: String, value: String, hint: String, widthPx: Int,
    type: InputType<String> = InputType.Text, onChange: (String) -> Unit
) {
    val colors = AppTheme.colors
    Div {
        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) { Text(label) }
        Input(type) {
            value(value)
            onInput { onChange(it.value) }
            if (hint.isNotEmpty()) placeholder(hint)
            style { formInputStyle(colors); width(widthPx.px) }
        }
    }
}

/** datetime-local values are browser-local; the API takes ISO instants. */
private fun HistoryFilters.withIsoDates(): HistoryFilters = copy(from = localToIso(from), to = localToIso(to))

private fun localToIso(local: String): String {
    if (local.isBlank()) return ""
    return try { kotlin.js.Date(local).toISOString() } catch (_: Throwable) { "" }
}

private const val TWO_DIGIT = "2-digit"

/** UTC instant shown in Africa/Cairo time, with daylight saving handled by the browser. */
internal fun cairoTime(isoUtc: String): String {
    val options = dateLocaleOptions {
        timeZone = "Africa/Cairo"
        hour12 = false
        year = "numeric"
        month = TWO_DIGIT
        day = TWO_DIGIT
        hour = TWO_DIGIT
        minute = TWO_DIGIT
        second = TWO_DIGIT
    }
    return try {
        kotlin.js.Date(isoUtc).toLocaleString("en-GB", options)
    } catch (_: Throwable) {
        isoUtc
    }
}
